import React, { useState, useEffect, useRef } from 'react';
import './DossierScreen.css';

// Ticker tape sound effect - create instance per component
const createTickerAudio = () => {
  let ctx = null;
  let tickInterval = null;

  const init = () => {
    if (ctx) return;
    ctx = new (window.AudioContext || window.webkitAudioContext)();
  };

  const tick = () => {
    if (!ctx) return;
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 800 + Math.random() * 400;
      osc.type = 'square';
      gain.gain.setValueAtTime(0.08, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.02);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.02);
    } catch (e) {
      // Ignore audio errors
    }
  };

  const startTicker = () => {
    init();
    stopTicker(); // Clear any existing interval first
    tickInterval = setInterval(() => {
      if (Math.random() < 0.7) tick();
    }, 30);
  };

  const stopTicker = () => {
    if (tickInterval) {
      clearInterval(tickInterval);
      tickInterval = null;
    }
  };

  const revealSound = () => {
    init();
    if (!ctx) return;
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(200, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(400, ctx.currentTime + 0.1);
      gain.gain.setValueAtTime(0.1, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.15);
    } catch (e) {
      // Ignore audio errors
    }
  };

  const cleanup = () => {
    stopTicker();
    if (ctx) {
      try {
        ctx.close();
      } catch (e) {
        // Ignore
      }
      ctx = null;
    }
  };

  return { startTicker, stopTicker, revealSound, cleanup };
};

const DossierScreen = ({ vuln, level, totalLevels, score, credibility, onContinue, onAbandon }) => {
  const [revealStep, setRevealStep] = useState(0);
  const [showButtons, setShowButtons] = useState(false);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    // Create new audio instance for this component
    audioRef.current = createTickerAudio();
    const audio = audioRef.current;

    // Start ticker
    audio.startTicker();

    const steps = 6;
    let current = 0;

    timerRef.current = setInterval(() => {
      if (current < steps) {
        setRevealStep(current + 1);
        audio.revealSound();
        current++;
      } else {
        clearInterval(timerRef.current);
        timerRef.current = null;
        audio.stopTicker();
        setTimeout(() => setShowButtons(true), 300);
      }
    }, 400);

    // Cleanup function - CRITICAL
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.cleanup();
        audioRef.current = null;
      }
    };
  }, [vuln.id]); // Re-run when vuln changes

  // Also stop audio when navigating away
  const handleContinue = () => {
    if (audioRef.current) {
      audioRef.current.cleanup();
    }
    onContinue();
  };

  const handleAbandon = () => {
    if (audioRef.current) {
      audioRef.current.cleanup();
    }
    onAbandon();
  };

  const dangerBar = () => {
    const filled = vuln.dangerLevel;
    const empty = 10 - filled;
    return '█'.repeat(filled) + '░'.repeat(empty);
  };

  const getDifficultyLabel = (rank) => {
    if (rank >= 9) return { label: 'ROOKIE', color: '#7CFC00' };
    if (rank >= 7) return { label: 'TRAINED', color: '#ADFF2F' };
    if (rank >= 5) return { label: 'VETERAN', color: '#FFD700' };
    if (rank >= 3) return { label: 'EXPERT', color: '#FF8C00' };
    return { label: 'NIGHTMARE', color: '#FF4444' };
  };

  const difficulty = getDifficultyLabel(vuln.rank);

  return (
    <div className="dossier-screen">
      <div className="dossier-header">
        <div className="header-left">
          <span className="level-badge">BOUNTY {level}/{totalLevels}</span>
          <span className="score">SCORE: {score.toLocaleString()}</span>
        </div>
        <div className="header-right">
          <span className="difficulty-badge" style={{ color: difficulty.color, borderColor: difficulty.color }}>
            {difficulty.label}
          </span>
          <span className={`credibility ${credibility < 30 ? 'danger' : credibility < 60 ? 'warning' : ''}`}>
            CRED: {credibility}%
          </span>
        </div>
      </div>

      <div className="dossier-container">
        <div className="dossier-border">
          <div className="dossier-title">
            <span className="classified">▓▓ CLASSIFIED ▓▓</span>
            <h1>TARGET DOSSIER</h1>
          </div>

          {revealStep >= 1 && (
            <div className="dossier-section target-header">
              <div className="target-photo">
                <pre className="target-ascii">{vuln.boss.ascii}</pre>
              </div>
              <div className="target-info">
                <h2 className="target-id">{vuln.id}: {vuln.name}</h2>
                <div className="target-meta">
                  <p><span className="label">CODENAME:</span> {vuln.shortName}</p>
                  <p><span className="label">BOUNTY:</span> <span className="bounty">{vuln.bounty.toLocaleString()} pts</span></p>
                  <p>
                    <span className="label">DANGER:</span>
                    <span className="danger-bar" style={{ color: vuln.color }}> {dangerBar()}</span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {revealStep >= 2 && (
            <div className="dossier-section">
              <h3>► KNOWN ALIASES</h3>
              <ul className="alias-list">
                {vuln.dossier.aliases.map((alias, i) => (
                  <li key={i}>• {alias}</li>
                ))}
              </ul>
            </div>
          )}

          {revealStep >= 3 && (
            <div className="dossier-section">
              <h3>► MODUS OPERANDI</h3>
              <p className="description">{vuln.dossier.description}</p>
              <ul className="mo-list">
                {vuln.dossier.modesOperandi.map((mo, i) => (
                  <li key={i}>→ {mo}</li>
                ))}
              </ul>
            </div>
          )}

          {revealStep >= 4 && (
            <div className="dossier-section">
              <h3>► EVIDENCE FOUND AT CRIME SCENES</h3>
              <div className="evidence-grid">
                {vuln.dossier.evidenceAtCrimeScenes.map((evidence, i) => (
                  <code key={i} className="evidence-item">{evidence}</code>
                ))}
              </div>
            </div>
          )}

          {revealStep >= 5 && (
            <div className="dossier-section">
              <h3>► FORTIFY / COVERITY FLAGS</h3>
              <div className="flags-container">
                <div className="flag-group">
                  <span className="flag-label">FORTIFY:</span>
                  {vuln.dossier.fortifyFlags.map((flag, i) => (
                    <span key={i} className="flag">{flag}</span>
                  ))}
                </div>
                <div className="flag-group">
                  <span className="flag-label">COVERITY:</span>
                  {vuln.dossier.coverityCheckers.map((checker, i) => (
                    <span key={i} className="flag coverity">{checker}</span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {revealStep >= 6 && (
            <div className="dossier-section victim-section">
              <h3>► KNOWN VICTIM</h3>
              <div className="victim-card">
                <span className="victim-name">{vuln.dossier.realWorldVictim.name}</span>
                <p className="victim-desc">{vuln.dossier.realWorldVictim.description}</p>
              </div>
            </div>
          )}

          {showButtons && (
            <div className="dossier-footer">
              <p className="mission-text">YOUR MISSION: Hunt and neutralize this threat.</p>
              <div className="dossier-buttons">
                <button className="btn abandon-btn" onClick={handleAbandon}>
                  [ ABANDON HUNT ]
                </button>
                <button className="btn continue-btn" onClick={handleContinue}>
                  [ BEGIN HUNT ]
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DossierScreen;
